/**
 * AccountantDashboard — Role-specific dashboard for internal accountants
 * 
 * Assembles shared components relevant to the accountant role:
 * - Queue stats with claim/release workflow
 * - ForensicInsightsCard: data anomaly analysis per case
 * - InsuranceQuestionsCard: AI-generated adjuster questions
 * - NextActionsChecklist: dynamic next steps per case
 * - IndustryBenchmark: claim vs industry comparison
 * 
 * Does NOT include data specialist widgets (TopPriorities, DataQuality, Alerts).
 * Uses RoleDashboardLayout for app chrome + AgentNotification.
 * 
 * NOTE: This is the INTERNAL accountant view (main app auth).
 * The EXTERNAL accountant portal lives at pages/AccountantDashboard.tsx
 * with its own AccountantAuthContext.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import RoleDashboardLayout from '../../components/RoleDashboardLayout';
import ForensicInsightsCard from '../../components/dashboard/ForensicInsightsCard';
import InsuranceQuestionsCard from '../../components/dashboard/InsuranceQuestionsCard';
import NextActionsChecklist from '../../components/dashboard/NextActionsChecklist';
import IndustryBenchmarkCard from '../../components/dashboard/IndustryBenchmarkCard';
import AIActivityCard from '../../components/dashboard/AIActivityCard';
import {
  FileText,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Lock,
  Unlock,
  ChevronRight,
  Loader2,
  Search,
} from 'lucide-react';

interface QueueStats {
  total_cases: number;
  needs_attention: number;
  no_action_needed: number;
  accountant: number;
  ready: number;
  total_value: number;
}

interface CaseItem {
  id: string;
  business_name: string;
  law_firm: string | null;
  event_type: string | null;
  assigned_queue: string;
  queue_reason: string;
  estimated_value: number;
  case_strength: number;
  completion_percentage: number;
  days_stale: number;
  is_claimed?: boolean;
  claimed_by_name?: string;
  primary_action: string;
  primary_action_url: string;
}

export default function AccountantDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, queueRes] = await Promise.allSettled([
        api.get<QueueStats>('/api/v1/queue/stats?user_role=accountant', { showErrorToast: false }),
        api.get<{ cases: CaseItem[] }>('/api/v1/queue/accountant?page=1&page_size=50', { showErrorToast: false }),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (queueRes.status === 'fulfilled') setCases(queueRes.value.cases || []);
    } catch (err) {
      console.warn('Failed to load accountant dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const filteredCases = cases.filter((c) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.business_name?.toLowerCase().includes(q) ||
      c.law_firm?.toLowerCase().includes(q)
    );
  });

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

  const getStrengthBadge = (strength: number) => {
    if (strength >= 80) return { label: 'Strong', color: 'bg-green-100 text-green-700' };
    if (strength >= 50) return { label: 'Moderate', color: 'bg-yellow-100 text-yellow-700' };
    return { label: 'Weak', color: 'bg-red-100 text-red-700' };
  };

  return (
    <RoleDashboardLayout role="accountant">
      <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Accountant Dashboard</h1>
            <p className="text-slate-500 mt-1">Review calculations, verify data, sign off on cases</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-100">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.accountant || 0}</p>
                <p className="text-sm text-slate-500">Available</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-100">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.needs_attention || 0}</p>
                <p className="text-sm text-slate-500">Need Review</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-green-100">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats?.ready || 0}</p>
                <p className="text-sm text-slate-500">Ready</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-100">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(stats?.total_value || 0)}</p>
                <p className="text-sm text-slate-500">Portfolio</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content: Case List + Sidebar Widgets */}
        <div className="flex gap-6">
          {/* Case List */}
          <div className="flex-1 min-w-0">
            <div className="mb-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Loading cases...</p>
                </div>
              ) : filteredCases.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <p className="font-medium text-slate-900">No cases to review</p>
                  <p className="text-sm text-slate-500 mt-1">All cases are being worked on.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredCases.map((c) => {
                    const strength = getStrengthBadge(c.case_strength || 0);
                    const isSelected = selectedCaseId === c.id;
                    return (
                      <div
                        key={c.id}
                        onClick={() => setSelectedCaseId(isSelected ? null : c.id)}
                        className={`px-5 py-4 cursor-pointer transition-colors flex items-center gap-4 ${
                          isSelected ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900 truncate">{c.business_name || 'Unknown'}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${strength.color}`}>
                              {strength.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {c.law_firm || 'No law firm'} • {c.primary_action}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-semibold text-slate-900">{formatCurrency(c.estimated_value || 0)}</p>
                          <p className="text-xs text-slate-400">{c.completion_percentage}% complete</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar: Accountant Widgets */}
          <div className="w-80 flex-shrink-0 space-y-4 hidden lg:block max-h-[calc(100vh-200px)] overflow-y-auto">
            {selectedCaseId ? (
              <>
                <ForensicInsightsCard reportId={selectedCaseId} />
                <InsuranceQuestionsCard reportId={selectedCaseId} />
                <NextActionsChecklist reportId={selectedCaseId} />
                <IndustryBenchmarkCard reportId={selectedCaseId} />
                <AIActivityCard reportId={selectedCaseId} />
              </>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-6 text-center shadow-sm">
                <div className="text-slate-400 mb-2">
                  <CheckCircle className="w-10 h-10 mx-auto" />
                </div>
                <h3 className="text-slate-700 font-medium mb-1">Select a Case</h3>
                <p className="text-slate-500 text-sm">
                  Click a case to view forensic insights, adjuster questions, and industry benchmarks.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </RoleDashboardLayout>
  );
}
